import type { SkillVersions } from '@elara/domain/flows';
import type { RetryEvent } from '@elara/domain/types';

import { truncate } from 'lodash-es';

import { extractOpenRouterGenerationId, stripKnownProviderPrefix } from '@elara/ai-shared/helpers';
import { SkillVersionsSchema } from '@elara/domain/flows';

export interface DebugUsageMetrics {
  cost?: number;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  modelName?: string;
  modelBadgeName?: string;
  /** Langfuse prompt version staff picked in Inspect for this reply. */
  promptVersion?: number;
  /** Reasoning effort staff picked in Inspect for this reply. */
  reasoningEffort?: string;
  skillVersions?: SkillVersions;
  retryCount?: number;
  retryReasons?: string[];
  openRouterGenerationId?: string;
  openRouterGenerationUrl?: string;
  runId?: string;
  traceId?: string;
  mastraStudioUrl?: string;
}

const OPENROUTER_GENERATION_BASE_URL = 'https://openrouter.ai/api/v1/generation?id=';
const MASTRA_STUDIO_OBSERVABILITY_PATH = '/admin/mastra/observability';

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

function toNonNegativeNumber(value: unknown): number | undefined {
  let parsed: number | undefined;
  if (typeof value === 'number') {
    parsed = value;
  } else if (typeof value === 'string' && value.trim().length > 0) {
    parsed = Number(value);
  }
  if (parsed === undefined || !Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }
  return parsed;
}

function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed;
}

function resolveOpenRouterGenerationId({
  metadataRecord,
  responseRecord,
  openRouterMetadata,
}: {
  metadataRecord: Record<string, unknown>;
  responseRecord?: Record<string, unknown>;
  openRouterMetadata?: Record<string, unknown>;
}): string | undefined {
  const openRouterGeneration = asRecord(openRouterMetadata?.generation);

  const candidates: unknown[] = [
    metadataRecord.openRouterGenerationId,
    metadataRecord.openrouterGenerationId,
    metadataRecord.responseId,
    responseRecord?.id,
    openRouterMetadata?.generationId,
    openRouterMetadata?.generation_id,
    openRouterMetadata?.id,
    openRouterGeneration?.id,
    openRouterMetadata,
  ];

  for (const candidate of candidates) {
    const generationId = extractOpenRouterGenerationId(candidate);
    if (generationId) return generationId;
  }

  return;
}

function buildMastraStudioUrl({ runId, traceId }: { runId?: string; traceId?: string }): string | undefined {
  if (!runId && !traceId) return;

  const query = new URLSearchParams();
  if (runId) query.set('runId', runId);
  if (traceId) query.set('traceId', traceId);
  const queryString = query.toString();
  if (!queryString) return MASTRA_STUDIO_OBSERVABILITY_PATH;
  return `${MASTRA_STUDIO_OBSERVABILITY_PATH}?${queryString}`;
}

export function resolveDebugUsageMetrics(metadata: unknown): DebugUsageMetrics {
  const metadataRecord = asRecord(metadata);
  if (!metadataRecord) return {};

  const generation = asRecord(metadataRecord.generation);
  const durationMs = toNonNegativeNumber(generation?.durationMs);

  const totalUsage = asRecord(metadataRecord.totalUsage);
  const usage = asRecord(metadataRecord.usage);
  const responseRecord = asRecord(metadataRecord.response);
  const providerMetadata = asRecord(metadataRecord.providerMetadata);
  const openRouterMetadata = asRecord(providerMetadata?.openrouter);
  const openRouterUsage = asRecord(openRouterMetadata?.usage);

  const inputFromUsage = toNonNegativeNumber(metadataRecord.inputTokens) || toNonNegativeNumber(totalUsage?.inputTokens ?? usage?.inputTokens);
  const outputFromUsage = toNonNegativeNumber(metadataRecord.outputTokens) || toNonNegativeNumber(totalUsage?.outputTokens ?? usage?.outputTokens);
  const promptFromProvider = toNonNegativeNumber(openRouterUsage?.promptTokens);
  const completionFromProvider = toNonNegativeNumber(openRouterUsage?.completionTokens);
  const cost = toNonNegativeNumber(metadataRecord.cost) || toNonNegativeNumber(openRouterUsage?.cost);
  const modelName = toNonEmptyString(metadataRecord.model) ?? toNonEmptyString(metadataRecord.modelId);
  const promptVersion = toNonNegativeNumber(metadataRecord.promptVersion);
  const reasoningEffort = toNonEmptyString(metadataRecord.reasoningEffort);
  const skillVersions = SkillVersionsSchema.safeParse(metadataRecord.skillVersions).data;
  const runId = toNonEmptyString(metadataRecord.runId);
  const traceId = toNonEmptyString(metadataRecord.traceId);
  const openRouterGenerationId = resolveOpenRouterGenerationId({ metadataRecord, responseRecord, openRouterMetadata });
  const openRouterGenerationUrl = openRouterGenerationId
    ? `${OPENROUTER_GENERATION_BASE_URL}${encodeURIComponent(openRouterGenerationId)}`
    : undefined;
  const mastraStudioUrl = buildMastraStudioUrl({ runId, traceId });

  const retries = asRecord(metadataRecord.retries) as { count: 1; attempts: RetryEvent[] };
  const retryCount = toNonNegativeNumber(retries?.count);

  return {
    cost,
    durationMs,
    inputTokens: inputFromUsage ?? promptFromProvider,
    outputTokens: outputFromUsage ?? completionFromProvider,
    modelName,
    modelBadgeName: modelName ? stripKnownProviderPrefix(modelName) : undefined,
    retryCount: retryCount && retryCount > 0 ? retryCount : undefined,
    retryReasons: retries?.attempts?.map((attempt) => {
      return `${attempt.error.message} - ${truncate(attempt.error.raw, { length: 100 })}`;
    }),
    ...(promptVersion ? { promptVersion } : {}),
    ...(reasoningEffort ? { reasoningEffort } : {}),
    ...(skillVersions && Object.keys(skillVersions).length > 0 ? { skillVersions } : {}),
    ...(openRouterGenerationId ? { openRouterGenerationId } : {}),
    ...(openRouterGenerationUrl ? { openRouterGenerationUrl } : {}),
    ...(runId ? { runId } : {}),
    ...(traceId ? { traceId } : {}),
    ...(mastraStudioUrl ? { mastraStudioUrl } : {}),
  };
}
